/**
 * The Shopify Forms app renders inside <shopify-forms-embed>'s open shadow DOM
 * with `novalidate`, so HTML5 validation is suppressed and the built-in error
 * UI only fires on submit. This script adds inline on-blur validation for the
 * required fields used by the Home Club signup form.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var EMBED_TAG = 'shopify-forms-embed';
  var TEXT_FIELDS = [
    { id: 'first_name',      label: 'first name' },
    { id: 'last_name',       label: 'last name' },
    { id: 'email',           label: 'email address', type: 'email' },
    { id: 'phone_number',    label: 'phone number' },
    { id: 'custom#postcode', label: 'postcode' }
  ];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var ERROR_CLASS = 'esr-blur-error';
  var STYLE_FLAG = 'data-esr-blur-validation';
  var BOUND_FLAG = 'esrBlurBound';

  function escapeId(id) {
    if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(id);
    return id.replace(/([^a-zA-Z0-9_-])/g, '\\$1');
  }

  function injectStyles(root) {
    if (root.querySelector('style[' + STYLE_FLAG + ']')) return;
    var style = document.createElement('style');
    style.setAttribute(STYLE_FLAG, '');
    // Match the theme's .field--error treatment: a red bottom border on the
    // control (not a full box) + a 12px error message (.field__message--error).
    // Design tokens resolve through the shadow boundary from :root.
    style.textContent =
      'output.' + ERROR_CLASS + '{' +
        'display:block;' +
        'margin:0;' +
        'font-family:var(--font-body);' +
        'font-weight:var(--font-weight-regular);' +
        'font-size:var(--text-body-small);' +
        'line-height:var(--line-height-body);' +
        'color:var(--color-error);' +
      '}' +
      'input[aria-invalid="true"],button[aria-invalid="true"]{' +
        'border-bottom-color:var(--color-error) !important;' +
      '}';
    root.appendChild(style);
  }

  function getFormRow(node, form) {
    var current = node;
    while (current && current.parentElement && current.parentElement !== form) {
      current = current.parentElement;
    }
    return current;
  }

  function findErrors(form, key) {
    return form.querySelectorAll('.' + ERROR_CLASS + '[data-for="' + key + '"]');
  }

  function showError(input, key, row, message) {
    input.setAttribute('aria-invalid', 'true');
    var form = row.closest('form');
    if (!form) return;
    var existing = findErrors(form, key);
    if (existing.length) {
      for (var i = 1; i < existing.length; i++) existing[i].remove();
      existing[0].textContent = message;
      return;
    }
    var msg = document.createElement('output');
    msg.setAttribute('role', 'alert');
    msg.className = ERROR_CLASS;
    msg.setAttribute('data-for', key);
    msg.id = ERROR_CLASS + '-' + key.replace(/[^a-zA-Z0-9_-]/g, '-');
    msg.textContent = message;
    row.insertAdjacentElement('afterend', msg);
    if (input.setAttribute) {
      var describedBy = input.getAttribute('aria-describedby');
      if (!describedBy || describedBy.indexOf(msg.id) === -1) {
        input.setAttribute('aria-describedby',
          describedBy ? describedBy + ' ' + msg.id : msg.id);
      }
    }
  }

  function clearError(input, key, row) {
    input.setAttribute('aria-invalid', 'false');
    var form = row.closest('form');
    if (!form) return;
    var existing = findErrors(form, key);
    if (!existing.length) return;
    var ids = [];
    existing.forEach(function (el) { if (el.id) ids.push(el.id); el.remove(); });
    if (!input.getAttribute) return;
    var describedBy = input.getAttribute('aria-describedby');
    if (!describedBy) return;
    var next = describedBy.split(/\s+/).filter(function (id) {
      return id && ids.indexOf(id) === -1;
    }).join(' ');
    if (next) input.setAttribute('aria-describedby', next);
    else input.removeAttribute('aria-describedby');
  }

  function validate(field, value) {
    var trimmed = (value || '').trim();
    if (!trimmed) return 'Please enter your ' + field.label + '.';
    if (field.type === 'email' && !EMAIL_RE.test(trimmed)) {
      return 'Please enter a valid email address.';
    }
    return null;
  }

  function bindTextField(root, form, field, state) {
    var input = root.getElementById ? root.getElementById(field.id) : null;
    if (!input) input = root.querySelector('input#' + escapeId(field.id));
    if (!input || input.dataset[BOUND_FLAG]) return;
    input.dataset[BOUND_FLAG] = '1';

    var row = getFormRow(input, form);
    if (!row) return;

    input.addEventListener('blur', function () {
      if (state.submitted) return;
      var error = validate(field, input.value);
      if (error) showError(input, field.id, row, error);
      else clearError(input, field.id, row);
    });

    input.addEventListener('input', function () {
      if (state.submitted) return;
      if (input.getAttribute('aria-invalid') !== 'true') return;
      if (!validate(field, input.value)) clearError(input, field.id, row);
    });
  }

  function bindPreferredStore(root, form, state) {
    var button = root.querySelector(
      'button[aria-controls="custom#preferred_store-selector-dropdown"]'
    );
    if (!button || button.dataset[BOUND_FLAG]) return;
    button.dataset[BOUND_FLAG] = '1';

    var row = getFormRow(button, form);
    if (!row) return;

    var textSpan = button.querySelector('span');
    var KEY = 'preferred_store';

    function evaluate() {
      if (state.submitted) return;
      var filled = textSpan && textSpan.textContent && textSpan.textContent.trim().length > 0;
      if (filled) {
        button.setAttribute('aria-invalid', 'false');
        findErrors(form, KEY).forEach(function (el) { el.remove(); });
      } else {
        button.setAttribute('aria-invalid', 'true');
        var existing = findErrors(form, KEY);
        if (existing.length) {
          for (var i = 1; i < existing.length; i++) existing[i].remove();
          return;
        }
        var msg = document.createElement('output');
        msg.setAttribute('role', 'alert');
        msg.className = ERROR_CLASS;
        msg.setAttribute('data-for', KEY);
        msg.textContent = 'Please select your preferred store.';
        row.insertAdjacentElement('afterend', msg);
      }
    }

    button.addEventListener('blur', function () {
      setTimeout(evaluate, 120);
    });

    if (textSpan) {
      new MutationObserver(evaluate).observe(textSpan, {
        childList: true, characterData: true, subtree: true
      });
    }
  }

  function setupForm(root) {
    var form = root.querySelector('form[data-testid="form"]');
    if (!form) return false;
    injectStyles(root);
    var state = { submitted: false };
    // Once Shopify's own submit-time error UI takes over, retire ours to avoid
    // stacked duplicate messages.
    function suspend() {
      state.submitted = true;
      form.querySelectorAll('.' + ERROR_CLASS).forEach(function (el) { el.remove(); });
    }
    form.addEventListener('submit', suspend, true);
    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.addEventListener('click', suspend, true);
    TEXT_FIELDS.forEach(function (field) { bindTextField(root, form, field, state); });
    bindPreferredStore(root, form, state);
    return true;
  }

  // GA4 sign_up (#1183): the Home Club form has completed. Keyed on the app's
  // success banner (the same hook shopify-forms-success-message uses) rather
  // than the submit, so a submit that fails validation does not count. Once
  // per page; the banner re-renders if the form restarts.
  function watchSignUp(root) {
    var sent = false;
    function check() {
      if (sent || !root.querySelector('[data-testid="banner-success"]')) return;
      sent = true;
      if (window.esTrack) {
        window.esTrack('sign_up', {
          method: 'home_club',
          form_location: window.location.pathname
        });
      }
    }
    check();
    new MutationObserver(check).observe(root, { childList: true, subtree: true });
  }

  function watchShadow(host) {
    if (!host.shadowRoot) {
      var attachObs = new MutationObserver(function () {
        if (host.shadowRoot) {
          attachObs.disconnect();
          watchShadow(host);
        }
      });
      attachObs.observe(host, { childList: true, subtree: true, attributes: true });
      return;
    }
    var root = host.shadowRoot;
    watchSignUp(root);
    if (setupForm(root)) return;
    var rootObs = new MutationObserver(function () {
      if (setupForm(root)) rootObs.disconnect();
    });
    rootObs.observe(root, { childList: true, subtree: true });
  }

  function init() {
    var host = document.querySelector(EMBED_TAG);
    if (host) { watchShadow(host); return; }
    var docObs = new MutationObserver(function () {
      var found = document.querySelector(EMBED_TAG);
      if (found) {
        docObs.disconnect();
        watchShadow(found);
      }
    });
    docObs.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
